import React, { useEffect, useMemo, useState } from 'react';
import { salesService } from '../../../services/salesService';
import { expenseService } from '../../../services/expenseService';

const fmt = (n: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP' }).format(n);

const DashboardTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodDays, setPeriodDays] = useState(30);
  const [sales, setSales] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - periodDays + 1);
        const [s, e] = await Promise.all([
          salesService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
          expenseService.list({ from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10), limit: 500 }),
        ]);
        setSales(s);
        setExpenses(e);
      } catch (err: any) {
        console.error('Dashboard load error', err);
        setError(err?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [periodDays]);

  const { revenue, spend, grossProfit, marginPct, dailyBurn, runwayDays } = useMemo(() => {
    const revenue = sales.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const spend = expenses.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const grossProfit = revenue - spend;
    const marginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;
    const dailyBurn = spend / Math.max(1, periodDays);
    const runwayDays = grossProfit > 0 ? Infinity : (revenue > 0 ? Math.round(Math.abs(grossProfit) / Math.max(1, dailyBurn)) : 0);
    return { revenue, spend, grossProfit, marginPct, dailyBurn, runwayDays };
  }, [sales, expenses, periodDays]);

  // Category breakdowns
  const salesByCategory = useMemo(() => {
    const map = new Map<string, number>();
    sales.forEach(s => {
      const cat = s.category || 'Uncategorized';
      map.set(cat, (map.get(cat) || 0) + Number(s.amount || 0));
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [sales]);

  const expensesByCategory = useMemo(() => {
    const map = new Map<string, number>();
    expenses.forEach(e => {
      const cat = e.category || 'Uncategorized';
      map.set(cat, (map.get(cat) || 0) + Number(e.amount || 0));
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [expenses]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-gray-100">
        <div className="text-gray-500">Loading dashboard data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-red-50 p-6 text-red-700 shadow-sm ring-1 ring-red-100">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Financial Overview</h3>
        <select
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={periodDays}
          onChange={(e) => setPeriodDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      {/* Primary Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 p-6 shadow-sm ring-1 ring-green-100">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-green-700">Revenue</div>
            <div className="text-2xl">💰</div>
          </div>
          <div className="mt-2 text-3xl font-bold text-green-900">{fmt(revenue)}</div>
          <div className="mt-1 text-xs text-green-600">{sales.length} transaction{sales.length !== 1 ? 's' : ''}</div>
        </div>

        <div className="rounded-xl bg-gradient-to-br from-red-50 to-rose-50 p-6 shadow-sm ring-1 ring-red-100">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-red-700">Expenses</div>
            <div className="text-2xl">💸</div>
          </div>
          <div className="mt-2 text-3xl font-bold text-red-900">{fmt(spend)}</div>
          <div className="mt-1 text-xs text-red-600">{expenses.length} transaction{expenses.length !== 1 ? 's' : ''}</div>
        </div>

        <div className={`rounded-xl bg-gradient-to-br p-6 shadow-sm ring-1 ${
          grossProfit >= 0
            ? 'from-blue-50 to-cyan-50 ring-blue-100'
            : 'from-orange-50 to-amber-50 ring-orange-100'
        }`}>
          <div className="flex items-center justify-between">
            <div className={`text-sm font-medium ${grossProfit >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
              Gross Profit
            </div>
            <div className="text-2xl">{grossProfit >= 0 ? '📈' : '📉'}</div>
          </div>
          <div className={`mt-2 text-3xl font-bold ${grossProfit >= 0 ? 'text-blue-900' : 'text-orange-900'}`}>
            {fmt(grossProfit)}
          </div>
          <div className={`mt-1 text-xs ${grossProfit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
            {marginPct.toFixed(1)}% margin
          </div>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="text-sm text-gray-500">Margin %</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">{marginPct.toFixed(1)}%</div>
          <div className="mt-1 text-xs text-gray-500">
            {marginPct < 20 ? '⚠️ Low' : marginPct < 40 ? '✅ Moderate' : '🌟 Healthy'}
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="text-sm text-gray-500">Daily Burn Rate</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">{fmt(dailyBurn)}</div>
          <div className="mt-1 text-xs text-gray-500">Average per day</div>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <div className="text-sm text-gray-500">Runway (estimated)</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900">
            {runwayDays === Infinity ? '∞' : `${runwayDays} days`}
          </div>
          <div className="mt-1 text-xs text-gray-500">Based on current profit</div>
        </div>
      </div>

      {/* Category Breakdowns */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Top Sales Categories */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h4 className="mb-4 text-base font-semibold text-gray-900">Top Sales Categories</h4>
          {salesByCategory.length === 0 ? (
            <p className="text-sm text-gray-500">No sales recorded yet</p>
          ) : (
            <div className="space-y-3">
              {salesByCategory.map(([category, amount]) => {
                const pct = revenue > 0 ? (amount / revenue) * 100 : 0;
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">{category}</span>
                      <span className="text-gray-900">{fmt(amount)}</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-green-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{pct.toFixed(1)}% of total</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Expense Categories */}
        <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h4 className="mb-4 text-base font-semibold text-gray-900">Top Expense Categories</h4>
          {expensesByCategory.length === 0 ? (
            <p className="text-sm text-gray-500">No expenses recorded yet</p>
          ) : (
            <div className="space-y-3">
              {expensesByCategory.map(([category, amount]) => {
                const pct = spend > 0 ? (amount / spend) * 100 : 0;
                return (
                  <div key={category}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">{category}</span>
                      <span className="text-gray-900">{fmt(amount)}</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-100">
                      <div
                        className="h-full rounded-full bg-red-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{pct.toFixed(1)}% of total</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
